/* Corkys pairing block — shared behaviour for the PDP block and the cart drawer block.
 *
 * Two modes, distinguished by the data-pair-solo attribute on the wrapper:
 *
 *   PDP (no attribute)  — the page's product is not in the cart yet, so the add sends TWO lines:
 *                         the page product plus the chosen partner.
 *   Drawer (solo)       — whatever prompted the drawer is already in the cart, so the add sends ONE
 *                         line: the chosen coffee. Sending the page product again here would silently
 *                         double it, which is the bug this attribute exists to prevent.
 *
 * Both then go to /checkout, which is what the CTA promises.
 */
(function () {
  if (window.__corkysCoffeePairingBound) return;
  window.__corkysCoffeePairingBound = true;

  function syncSelection(wrap) {
    var picked = wrap.querySelector('.cofpair__radio:checked');
    if (!picked) {
      picked = wrap.querySelector('.cofpair__radio');
      if (picked) picked.checked = true;
    }
    if (!picked) return;
    var name = wrap.querySelector('[data-coffee-name]');
    var meta = wrap.querySelector('[data-coffee-meta]');
    var price = wrap.querySelector('[data-coffee-cta-price]');
    if (name) name.textContent = picked.getAttribute('data-name') || '';
    if (meta) {
      /* Coffees carry a fixed size/grind worth stating; records carry only their format, which is
         already in data-roast. The suffix is declared in Liquid so this stays mode-agnostic. */
      var suffix = wrap.getAttribute('data-meta-suffix');
      meta.textContent = [picked.getAttribute('data-roast'), suffix].filter(Boolean).join(' · ');
    }
    /* Rendered as "PAIR & CHECK OUT • +$17.98". The bullet comes from the theme's own price span, so
       the shape matches "ADD TO CART • $65.99". The + stays: unlike Add to cart, this figure is an
       ADD-ON, not what the shopper pays, and dropping it would read as though the record cost $17.98. */
    if (price) price.textContent = '+' + (picked.getAttribute('data-price') || '');
  }

  /* Horizontal scroll affordance. The arrow is CREATED here rather than in Liquid so it only ever
     exists when the row actually overflows — a four-blend row, or a release group that fits, never
     renders a control that would do nothing. Re-measured on resize because how many tiles fit is a
     function of column width. */
  function setupScroller(wrap) {
    var opts = wrap.querySelector('[data-coffee-opts]');
    var scroller = opts && opts.parentElement;
    if (!opts || !scroller) return;

    /* The button is rendered by Liquid so it can use the theme's own arrow icon (Theme settings >
       arrow style / line thickness). JS only decides whether it is shown. */
    var btn = scroller.querySelector('[data-coffee-next]');
    if (!btn) return;
    var created = !btn.dataset.cofpairBound;
    btn.dataset.cofpairBound = '1';

    function refresh() {
      var overflows = opts.scrollWidth - opts.clientWidth > 4;
      btn.hidden = !overflows;
      if (!overflows) return;
      var atEnd = opts.scrollLeft + opts.clientWidth >= opts.scrollWidth - 2;
      /* At the end the arrow turns around rather than becoming a dead control. Set the transform inline
         and INCLUDE translateY(-50%) so the button stays vertically centred — a bare scaleX(-1) dropped
         it below centre. Inline rather than a class: a matching `.cofpair__next--back { transform }`
         rule measured as not winning the cascade here, while the identical inline value applies cleanly. */
      btn.style.transform = atEnd ? 'translateY(-50%) scaleX(-1)' : '';
      btn.setAttribute('aria-label', atEnd ? 'Back to the first options' : 'Show more options');
    }

    if (created) {
      btn.addEventListener('click', function () {
        var tile = opts.querySelector('.cofpair__opt');
        var step = tile ? (tile.getBoundingClientRect().width + 8) * 2 : opts.clientWidth * 0.6;
        var atEnd = opts.scrollLeft + opts.clientWidth >= opts.scrollWidth - 2;
        /* No `behavior` here on purpose. The stylesheet already sets scroll-behavior: smooth on this
           element, and passing behavior:'smooth' as well produced NO scroll at all — measured: with
           it 0px, with 'auto' or the positional form 260px. The CSS supplies the animation. */
        opts.scrollBy({ left: atEnd ? -opts.scrollWidth : step });
        /* Driven from the click, not only from the scroll event: a <fieldset> does not reliably fire
           scroll in every engine, and measured here it never did — the arrow scrolled correctly but
           never turned around at the end. The timeout lets the smooth scroll settle first. */
        setTimeout(refresh, 450);
      });
      /* Kept as well, for drag and trackpad scrolling where it does fire. */
      opts.addEventListener('scroll', refresh, { passive: true });
      addEventListener('resize', refresh);
    }
    refresh();
  }

  function syncAll() {
    document.querySelectorAll('[data-coffee-pairing]').forEach(function (w) {
      syncSelection(w);
      setupScroller(w);
    });
  }

  /* Drawer visibility. Offering coffee to somebody who already has coffee in the cart is noise, and
     an empty cart has nothing to pair with. Decided from /cart.js rather than from Liquid because the
     drawer's markup may be re-rendered or DOM-patched depending on how the cart updates — reading the
     live cart is correct under either. Matching on product_type covers the whole coffee catalogue,
     not just the four offered here. */
  var updating = false;
  function refreshDrawerVisibility() {
    var blocks = document.querySelectorAll('[data-coffee-pairing][data-pair-solo]');
    if (!blocks.length) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var hasCoffee = items.some(function (i) { return i.product_type === 'Coffee'; });
        var show = items.length > 0 && !hasCoffee;
        updating = true;
        blocks.forEach(function (b) {
          b.hidden = !show;
          if (show) syncSelection(b);
        });
        setTimeout(function () { updating = false; }, 0);
      })
      .catch(function () { /* leave whatever state it is in rather than flashing the block */ });
  }

  /* The drawer's body is not in the initial DOM — the theme injects it when the cart is first fetched,
     which is AFTER this script's own load-time pass. It also does not reliably dispatch a cart event
     on that first render, so listening for one leaves the block stuck at its default hidden state
     (observed live: four options present, block never shown).
     Watching for the injection is the only signal available under every update path. */
  function observeCartRenders() {
    var root = document.querySelector('cart-element#cart-drawer')
      || document.querySelector('cart-element')
      || document.body;
    if (!root || root.__cofpairObserved) return;
    root.__cofpairObserved = true;
    var timer = null;
    new MutationObserver(function () {
      // Our own writes are childList mutations too; ignore them or this re-enters forever.
      if (updating) return;
      clearTimeout(timer);
      timer = setTimeout(refreshDrawerVisibility, 120);
    }).observe(root, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', function () {
    syncAll(); refreshDrawerVisibility(); observeCartRenders();
  });
  syncAll();
  refreshDrawerVisibility();
  observeCartRenders();

  document.addEventListener('change', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('cofpair__radio')) {
      var w = e.target.closest('[data-coffee-pairing]');
      if (w) syncSelection(w);
    }
  });

  /* Re-check whenever the cart changes or the drawer is opened. The theme dispatches these; the click
     fallback covers the cart toggle in themes/paths that do not. */
  ['cart:updated', 'cart:refresh', 'cart:build', 'cart:open'].forEach(function (ev) {
    document.addEventListener(ev, refreshDrawerVisibility);
  });
  document.addEventListener('click', function (e) {
    if (e.target.closest && e.target.closest('.cart__toggle, [data-cart-drawer-toggle]')) {
      setTimeout(refreshDrawerVisibility, 350);
    }
  });

  /* The page product's variant is read at CLICK time, not at render time, because the shopper can
     change the variant between page load and this button.

     The form's hidden id input is now the only authority. It used to be checked SECOND, behind
     sh-release-picker's active card, because on release-group products the picker hid the native form
     and its cards were the buy box. The picker became a dropdown of links on 2026-08-14 — there are no
     cards, and the form is visible and authoritative on every product — so that branch is gone. */
  /* Scope every page-product lookup to THIS block's own product-info. Unscoped selectors first-match
     a quick-add form on the related-products carousel, which carries its own [name=id]/[name=quantity].
     Restored 2026-08-14: this definition was clobbered when setupScroller was rewritten, and its
     absence threw "scopeFor is not defined" from the click handler on every pairing block — the button
     did nothing, no request, no error surfaced to the shopper. */
  function scopeFor(wrap) {
    return (wrap && wrap.closest('product-info')) || document;
  }

  function pageVariantId(root) {
    var form = root.querySelector('form[data-product-form] [name="id"]');
    return form ? form.value : null;
  }

  /* The visible quantity input is NOT inside the product form — it lives in .variant-qty-wrapper, a
     sibling of the form inside .product__form. Looking for it under the form finds nothing on this
     page and something wrong on a page that has quick-add cards. */
  function pageQuantity(root) {
    var q = root.querySelector('.variant-qty-wrapper [name="quantity"]')
      || root.querySelector('.product__form [name="quantity"]')
      || root.querySelector('[name="quantity"]');
    var n = q ? parseInt(q.value, 10) : 1;
    return n > 0 ? n : 1;
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-coffee-cta]');
    if (!btn || btn.disabled) return;
    e.preventDefault();

    var wrap = btn.closest('[data-coffee-pairing]');
    var picked = wrap && wrap.querySelector('.cofpair__radio:checked');
    var err = wrap && wrap.querySelector('[data-coffee-error]');
    var txt = btn.querySelector('[data-coffee-cta-text]');
    var partnerId = picked && picked.value;
    var solo = wrap && wrap.hasAttribute('data-pair-solo');

    if (err) { err.hidden = true; err.textContent = ''; }

    var items = [];
    if (!solo) {
      var root = scopeFor(wrap);
      var pageId = pageVariantId(root);
      if (!pageId) {
        if (err) { err.hidden = false; err.textContent = 'Something went wrong. Try Add to cart instead.'; }
        return;
      }
      items.push({ id: Number(pageId), quantity: pageQuantity(root) });
    }
    if (!partnerId) {
      if (err) { err.hidden = false; err.textContent = 'Something went wrong. Try Add to cart instead.'; }
      return;
    }
    items.push({ id: Number(partnerId), quantity: 1 });

    var original = txt.textContent;
    btn.disabled = true;
    txt.textContent = 'Adding…';

    /* One request with every line. A two-call sequence can leave the first item in the cart and the
       second not, then redirect to checkout anyway — the shopper would pay for half of what the
       button promised. /cart/add.js applies an items array atomically. */
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) {
          btn.disabled = false;
          txt.textContent = original;
          if (err) {
            err.hidden = false;
            err.textContent = (res.data && res.data.description)
              ? res.data.description
              : 'That combination is unavailable right now.';
          }
          return;
        }
        txt.textContent = 'Taking you to checkout…';
        window.location.href = '/checkout';
      })
      .catch(function () {
        btn.disabled = false;
        txt.textContent = original;
        if (err) { err.hidden = false; err.textContent = 'Network error. Try Add to cart instead.'; }
      });
  });
})();
